import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { AuthProvider } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "sonner";
import { installLoggerSink } from "@/lib/logger";
import { initClientSentry, clientSentrySink } from "@/lib/sentry.browser";

initClientSentry();
installLoggerSink(clientSentrySink);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page Not Found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  useEffect(() => {
    import("@sentry/react").then(({ captureException }) => {
      try {
        captureException(error);
      } catch {
        /* swallow */
      }
    });
  }, [error]);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CairnOS" },
      { name: "description", content: "Multi-tenant 3PL warehouse management system" },
      { property: "og:title", content: "CairnOS" },
      { property: "og:description", content: "Multi-tenant 3PL warehouse management system" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "theme-color", content: "#0b0f14" },
      // Defense-in-depth security headers via meta. Cookies/sessions still
      // rely on Supabase Auth's own controls; these stop opportunistic
      // attacks (clickjacking, MIME sniffing, browser feature abuse) and
      // restrict where the page can talk to.
      // CSP allows our own origin, Supabase (REST + Realtime ws), inline
      // styles (Tailwind injects), and data:/blob: for generated PDFs and
      // label previews. No unsafe-inline scripts.
      {
        httpEquiv: "Content-Security-Policy",
        content: [
          "default-src 'self'",
          `connect-src 'self' ${import.meta.env.VITE_SUPABASE_URL as string} wss://${new URL(import.meta.env.VITE_SUPABASE_URL as string).host} https: wss:`,
          "img-src 'self' data: blob: https:",
          "style-src 'self' 'unsafe-inline'",
          "script-src 'self' 'unsafe-inline' 'unsafe-eval' blob:",
          "font-src 'self' data:",
          "frame-ancestors 'self' https://lovable.dev https://*.lovable.dev https://*.lovable.app https://*.lovableproject.com",
          "base-uri 'self'",
          "form-action 'self'",
          "object-src 'none'",
        ].join("; "),
      },
      {
        httpEquiv: "Permissions-Policy",
        content: [
          "accelerometer=()",
          "autoplay=()",
          "camera=(self)", // barcode scanner pages opt in
          "geolocation=()",
          "gyroscope=()",
          "magnetometer=()",
          "microphone=()",
          "payment=()",
          "usb=()",
        ].join(", "),
      },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { httpEquiv: "X-Content-Type-Options", content: "nosniff" },
      { name: "twitter:title", content: "CairnOS" },
      { name: "twitter:description", content: "Multi-tenant 3PL warehouse management system" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/9HM5dFhS9edKooOScgxnaqPwYu33/social-images/social-1780524897418-Screenshot_2026-06-03_at_2.09.02_PM.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/9HM5dFhS9edKooOScgxnaqPwYu33/social-images/social-1780524897418-Screenshot_2026-06-03_at_2.09.02_PM.webp" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "manifest", href: "/manifest.webmanifest" },
      // Pay TLS/DNS cost before the first Supabase request so the initial
      // data fetch on each authenticated page starts ~100ms sooner.
      {
        rel: "preconnect",
        href: import.meta.env.VITE_SUPABASE_URL as string,
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthRefreshBridge />
        <Outlet />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AuthRefreshBridge() {
  const router = useRouter();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(() => {
      router.invalidate();
      queryClient.invalidateQueries();
    });

    return () => data.subscription.unsubscribe();
  }, [router, queryClient]);

  return null;
}
